import type { Dispatch, SetStateAction } from 'react';
import { memo, useEffect, useRef, useState } from 'react';

import { Flex } from '@/components/Flex';
import { HeaderInfo } from '@/components/Header/components';
import type { ContentfulMainHeaderFragment } from '@/graphql/contentful/api';
import { useHeaderOffSet } from '@/hooks/useHeaderOffSet';
import { useIsMdDown } from '@/hooks/useMedia';
import type { HeaderThemeKeys } from '@/theme/types/components';

import {
  useMobileMenuDrawerBottomTabIndex,
  useMobileMenuPanelTabIndex,
} from '../../hooks/useMobileMenuFocus';
import { MobileNav } from '../MobileNav';

import * as S from './MobileMenu.styles';

export interface MobileMenuProps {
  headerBackground: HeaderThemeKeys;
  hideLanguageSwitcher?: boolean;
  hidePhone?: boolean;
  hideSearch?: boolean;
  isMobileMenuOpen: boolean;
  isOpen: boolean;
  items: Partial<ContentfulMainHeaderFragment>;
  onClose: (isOpen: boolean) => void;
  setShoppingBagDrawerOpen: Dispatch<SetStateAction<boolean>>;
  shoppingBagDrawerOpen: boolean;
}

export const MobileMenu = memo(
  ({
    headerBackground,
    hideLanguageSwitcher,
    hidePhone,
    hideSearch,
    isMobileMenuOpen,
    isOpen,
    items,
    onClose,
    setShoppingBagDrawerOpen,
    shoppingBagDrawerOpen,
  }: MobileMenuProps) => {
    const top = useHeaderOffSet();
    const isMdDown = useIsMdDown();
    useEffect(() => {
      if (shoppingBagDrawerOpen && !isMdDown) {
        const mainContent = document.querySelector('main');
        const footer = document.querySelector('footer');
        if (mainContent) mainContent.style.display = 'none';
        if (footer) footer.style.display = 'none';
      } else {
        const mainContent = document.querySelector('main');
        const footer = document.querySelector('footer');
        if (mainContent) mainContent.style.display = 'block';
        if (footer) footer.style.display = 'block';
      }
    }, [shoppingBagDrawerOpen]);
    const [isOpenSubDrawer, setIsOpenSubDrawer] = useState<boolean>(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const bottomMenuRef = useRef<HTMLDivElement>(null);

    useMobileMenuPanelTabIndex(panelRef, isOpen);
    useMobileMenuDrawerBottomTabIndex(bottomMenuRef, isOpen, isOpenSubDrawer);

    return (
      <S.Wrapper
        id='mobile-menu'
        $isOpen={isOpen}
        $isOpenSubDrawer={isOpenSubDrawer}
        $top={top}
        data-test-id='MobileMenuWrapper'
      >
        <div
          ref={panelRef}
          aria-hidden={!isOpen}
          data-test-id='MobileMenuPanel'
          inert={isOpen ? undefined : true}
        >
          <Flex as='nav' flexDirection='column'>
            <MobileNav
              data-test-id='MobileMenuMobileNav'
              isOpenSubDrawer={isOpenSubDrawer}
              items={items as ContentfulMainHeaderFragment}
              onClose={onClose}
              setIsOpenSubDrawer={setIsOpenSubDrawer}
            />
          </Flex>
          <div ref={bottomMenuRef}>
            <S.BottomMenu data-test-id='MobileMenuBottomMenu'>
              <HeaderInfo
                data-test-id='MobileMenuHeaderInfo'
                headerBackground={headerBackground}
                hideLanguageSwitcher={hideLanguageSwitcher}
                hidePhone={hidePhone}
                hideSearch={hideSearch}
                isMobileFooter={true}
                isMobileMenuOpen={isMobileMenuOpen}
                onMobileMenuHandler={() => onClose(false)}
                onShoppingBagDrawerHandler={setShoppingBagDrawerOpen}
              />
            </S.BottomMenu>
          </div>
        </div>
      </S.Wrapper>
    );
  },
);

MobileMenu.displayName = 'Aside';
